import * as React from "react";

import { addCustomizationAreaToOrderProductionJobMutation } from "../../graph";
import { availableCustomizationAreaMethods } from "../../types/ProductionMethodOption";
import { filter } from "graphql-anywhere";
import { getIconForCustomizationAreaTemplate } from "../../helpers/getIconForCustomizationAreaTemplate";
import { getIconForProductionJobMethod } from "../../helpers/getIconForProductionJobMethod";
import { fragments as inkColorDetailsFragments } from "../InkColorDetails/graph";
import { makeStyles } from "@mui/styles";
import { query } from "./graph";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { vinylSizeOptions } from "../../types/VinylSizeOptions";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CustomizationAreaRadioList from "../CustomizationAreaForm/components/CustomizationAreaRadioList";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InkColorDetails from "../InkColorDetails";
import InkColorModal from "../InkColorModal";
import PendingStatusView from "../PendingStatusView";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import VinylCombobox from "../CustomizationAreaForm/components/VinylCombobox";

const useStyles = makeStyles(theme => ({
  addColorsButton: { marginBottom: theme.spacing(2) },

  colorCount: {
    marginBottom: theme.spacing(2)
  },

  printAreaMethodAttributesContainer: {
    display: "flex",
    marginBottom: theme.spacing(2)
  },

  areaContainer: {
    flex: 1
  },

  methodContainer: {
    flex: 1
  },

  list: {
    listStyle: "none",
    marginTop: 0,
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(1)
  },

  listItem: {
    marginBottom: theme.spacing(1)
  },

  attributesContainer: {
    flex: 0.6
  },

  inkColorContainer: {
    display: "flex",
    flexDirection: "column"
  },

  splitIntoColorsCheckbox: {
    width: "150px"
  }
}));

const defaultInkColorInputErrors = {
  colors: [],
  colorsInput: {
    inkColorId: [],
    pantoneColorId: []
  },
  orderProductionJobCustomizationAreaInkColorId: []
};

const defaultInputErrors = {
  autobase: [],
  colors: [],
  distressed: [],
  flatten: [],
  halftones: [],
  imageRepair: [],
  note: [],
  recreate: [],
  sponsorback: [],
  splitIntoColors: [],
  vinylConfiguration: [],
  vinylConfigurationInput: {
    names: [],
    namesColorId: [],
    namesFontId: [],
    numbers: [],
    numbersColorId: [],
    numbersFontId: [],
    size: []
  }
};

const AddCustomizationAreaModal = ({
  isOpen,
  onRequestClose,
  orderProductionJobId
}) => {
  const classes = useStyles();

  // Query:
  const { data, error, loading } = useQuery(query, {
    variables: { orderProductionJobId },
    skip: !isOpen
  });

  // Mutations:
  const [addCustomizationAreaToOrderProductionJob] = useMutation(
    addCustomizationAreaToOrderProductionJobMutation
  );

  // States:
  const [colorSelections, setColorSelections] = useState([]);
  const [generalErrors, setGeneralErrors] = useState([]);
  const [inputErrors, setInputErrors] = useState(defaultInputErrors);
  const [
    customizationAreaTemplateSelectedRadioValue,
    setCustomizationAreaRadioListValue
  ] = useState("Q3VzdG9taXphdGlvbkFyZWFUZW1wbGF0ZTox");
  const [inkColorSelectionInputErrors, setInkColorSelectionInputErrors] =
    useState(defaultInkColorInputErrors);
  const [isAddingCustomizationArea, setIsAddingCustomizationArea] =
    useState(false);
  const [
    productCustomizationMethodComboboxSelection,
    setProductCustomizationMethodComboboxSelection
  ] = useState("SCREEN_PRINTING");
  const [distressedCheckboxChecked, setDistressedCheckboxChecked] =
    useState(false);
  const [colorCountInputValue, setColorCountInputValue] = useState("0");
  const [isAddingColors, setIsAddingColors] = useState(false);
  const [autobaseCheckboxChecked, setAutobaseCheckboxChecked] = useState(false);
  const [customizationAreaNote, setCustomizationAreaNote] = useState("");
  const [flattenCheckboxChecked, setFlattenCheckboxChecked] = useState(false);
  const [halftonesCheckboxChecked, setHalftonesCheckboxChecked] =
    useState(false);
  const [imageRepairCheckboxChecked, setImageRepairCheckboxChecked] =
    useState(false);
  const [recreateCheckboxChecked, setRecreateCheckboxChecked] = useState(false);
  const [splitIntoColorsCheckboxChecked, setSplitIntoColorsCheckboxChecked] =
    useState(true);
  const [sponsorbackCheckboxChecked, setSponsorbackCheckboxChecked] =
    useState(false);
  const [vinylNamesCheckboxChecked, setVinylNamesCheckboxChecked] =
    useState(false);
  const [vinylFontSelection, setVinylFontSelection] = useState("");
  const [vinylColorSelection, setVinylColorSelection] = useState("");
  const [vinylNumbersCheckboxChecked, setVinylNumbersCheckboxChecked] =
    useState(false);
  const [vinylSizeComboboxSelection, setVinylSizeComboboxSelection] =
    useState(0);

  const availableOptions = useMemo(() => {
    const isCutVinyl =
      productCustomizationMethodComboboxSelection === "CUT_VINYL";

    const isPrintedVinyl =
      productCustomizationMethodComboboxSelection === "PRINTED_VINYL";

    const isDTG =
      productCustomizationMethodComboboxSelection === "DIRECT_TO_GARMENT";

    const isEmbroidery =
      productCustomizationMethodComboboxSelection === "EMBROIDERY";

    const isScreenPrinting =
      productCustomizationMethodComboboxSelection === "SCREEN_PRINTING";

    const isSublimation =
      productCustomizationMethodComboboxSelection === "SUBLIMATION";

    const isTackleTwill =
      productCustomizationMethodComboboxSelection === "TACKLE_TWILL";

    return {
      isCutVinyl,
      colorOptionIsAvailable: isScreenPrinting || isEmbroidery || isTackleTwill,
      distressedOptionIsAvailable: isScreenPrinting || isDTG || isSublimation,
      halftonesOptionIsAvailable: isScreenPrinting,
      flattenOptionIsAvailable:
        isScreenPrinting ||
        isCutVinyl ||
        isPrintedVinyl ||
        isEmbroidery ||
        isDTG,
      recreateOptionIsAvailable:
        isScreenPrinting ||
        isCutVinyl ||
        isPrintedVinyl ||
        isEmbroidery ||
        isDTG,
      sponsorbackOptionIsAvailable: isScreenPrinting || isDTG,
      autobaseOptionIsAvailable: isScreenPrinting,
      imageRepairOptionIsAvailable:
        isScreenPrinting || isCutVinyl || isPrintedVinyl || isDTG
    };
  }, [productCustomizationMethodComboboxSelection]);

  const {
    vinylConfigurationInput,
    distressed,
    autobase,
    halftones,
    flatten,
    note,
    recreate,
    sponsorback,
    imageRepair,
    splitIntoColors
  } = inputErrors;
  const {
    names,
    namesColorId,
    namesFontId,
    numbers,
    numbersColorId,
    numbersFontId,
    size
  } = vinylConfigurationInput;

  // Handlers:
  const addColorsSelection = newColorSelections =>
    setColorSelections([...newColorSelections]);

  const resetForm = () => {
    setInputErrors(defaultInputErrors);
    setIsAddingCustomizationArea(false);
    setIsAddingColors(false);
    setColorSelections([]);
    setColorCountInputValue("");
    setCustomizationAreaNote("");
    setGeneralErrors([]);
    setProductCustomizationMethodComboboxSelection(
      "Q3VzdG9taXphdGlvbkFyZWFUZW1wbGF0ZTox"
    );
    setProductCustomizationMethodComboboxSelection("SCREEN_PRINTING");
    setDistressedCheckboxChecked(false);
    setAutobaseCheckboxChecked(false);
    setFlattenCheckboxChecked(false);
    setHalftonesCheckboxChecked(false);
    setImageRepairCheckboxChecked(false);
    setInkColorSelectionInputErrors(defaultInkColorInputErrors);
    setRecreateCheckboxChecked(false);
    setSplitIntoColorsCheckboxChecked(true);
    setSponsorbackCheckboxChecked(false);
    setVinylNamesCheckboxChecked(false);
    setVinylColorSelection("");
    setVinylFontSelection("");
    setVinylNumbersCheckboxChecked(false);
    setVinylSizeComboboxSelection(0);
  };

  const resetGeneralErrors = () => setGeneralErrors([]);

  const resetInputErrors = () => setInputErrors(defaultInputErrors);

  const setVinylConfigurationNamesColorIdError =
    vinylConfigurationNamesColorIdError => {
      setInputErrors({
        ...inputErrors,
        vinylConfigurationInput: {
          ...vinylConfigurationInput,
          namesColorId: [vinylConfigurationNamesColorIdError]
        }
      });
    };

  const setVinylConfigurationNamesFontIdError =
    vinylConfigurationNamesFontIdError => {
      setInputErrors({
        ...inputErrors,
        vinylConfigurationInput: {
          ...vinylConfigurationInput,
          namesFontId: [vinylConfigurationNamesFontIdError]
        }
      });
    };

  const setVinylConfigurationNumbersColorIdError =
    vinylConfigurationNumbersColorIdError => {
      setInputErrors({
        ...inputErrors,
        vinylConfigurationInput: {
          ...vinylConfigurationInput,
          numbersColorId: [vinylConfigurationNumbersColorIdError]
        }
      });
    };

  const setVinylConfigurationNumbersFontIdError =
    vinylConfigurationNumbersFontIdError => {
      setInputErrors({
        ...inputErrors,
        vinylConfigurationInput: {
          ...vinylConfigurationInput,
          numbersFontId: [vinylConfigurationNumbersFontIdError]
        }
      });
    };

  const handleModalRequestClose = () => {
    onRequestClose();
    resetGeneralErrors();
    resetInputErrors();
  };

  const handleCancelButtonClick = () => {
    onRequestClose();
    resetForm();
    resetGeneralErrors();
    resetInputErrors();
  };

  const handleSplitIntoColorsCheckboxChange = checked =>
    setSplitIntoColorsCheckboxChecked(checked);

  const handleCustomizationAreaRadioListSelect =
    customizationAreaTemplateId => {
      setCustomizationAreaRadioListValue(customizationAreaTemplateId);
    };

  const handleColorCountInputChange = event => {
    if (
      !(
        parseInt(event.target.value, 10) > 20 ||
        parseInt(event.target.value, 10) < 0
      )
    ) {
      setColorCountInputValue(event.target.value);
    }
  };

  const handleAddColorsButtonClick = () => setIsAddingColors(true);

  const handleAddColorsModalRequestClose = () => setIsAddingColors(false);

  const handleAddColorsModalColorsAdded = (inkColors, pantoneColors) => {
    setIsAddingColors(true);
    addColorsSelection([
      ...inkColors.map(inkColor => ({
        inkColor
      })),
      ...pantoneColors.map(pantoneColor => ({
        pantoneColor
      }))
    ]);
    setIsAddingColors(false);
  };

  const handleProductCustomizationMethodComboboxSelect =
    productCustomizationMethod => {
      setProductCustomizationMethodComboboxSelection(
        productCustomizationMethod
      );
    };

  const handleAutobaseCheckboxChange = checked => {
    setAutobaseCheckboxChecked(checked);
  };

  const handleCustomizationAreaNoteChange = event => {
    setCustomizationAreaNote(event.target.value);
  };

  const handleDistressedCheckboxChange = checked => {
    setDistressedCheckboxChecked(checked);
  };

  const handleFlattenCheckboxChange = checked => {
    setFlattenCheckboxChecked(checked);
  };

  const handleHalftonesCheckboxChange = checked => {
    setHalftonesCheckboxChecked(checked);
  };

  const handleImageRepairCheckboxChange = checked => {
    setImageRepairCheckboxChecked(checked);
  };

  const handleRecreateCheckboxChange = checked => {
    setRecreateCheckboxChecked(checked);
  };

  const handleSponsorbackCheckboxChange = checked => {
    setSponsorbackCheckboxChecked(checked);
  };

  const handleVinylColorSelectorSelect = colorId => {
    setVinylColorSelection(colorId);
  };

  const handleVinylFontSelectorSelect = fontId => {
    setVinylFontSelection(fontId);
  };

  const handleVinylSizeComboboxSelect = selection => {
    setVinylSizeComboboxSelection(selection);
  };

  const handleVinylNamesCheckboxChange = checked => {
    setVinylNamesCheckboxChecked(checked);
  };

  const handleVinylNumbersCheckboxChange = checked => {
    setVinylNumbersCheckboxChecked(checked);
  };

  const handleFormSubmit = event => {
    event.preventDefault();

    let vinylConfigurationInputValues = null;

    if (
      availableOptions.isCutVinyl &&
      (vinylNamesCheckboxChecked || vinylNumbersCheckboxChecked)
    ) {
      vinylConfigurationInputValues = {
        names: vinylNamesCheckboxChecked,
        namesColorId: null,
        namesFontId: null,
        numbers: vinylNumbersCheckboxChecked,
        numbersColorId: null,
        numbersFontId: null,
        size: vinylSizeComboboxSelection
      };

      if (vinylNamesCheckboxChecked) {
        if (vinylColorSelection === "") {
          setVinylConfigurationNamesColorIdError("is required");
          return;
        } else {
          vinylConfigurationInputValues.namesColorId = vinylColorSelection;
        }

        if (vinylSizeComboboxSelection) {
          vinylConfigurationInputValues.size = vinylSizeComboboxSelection;
        }

        if (vinylFontSelection === "") {
          setVinylConfigurationNamesFontIdError("is required");
          return;
        } else {
          vinylConfigurationInputValues.namesFontId = vinylFontSelection;
        }
      }

      if (vinylNumbersCheckboxChecked) {
        if (vinylColorSelection === "") {
          setVinylConfigurationNumbersColorIdError("is required");
          return;
        } else {
          vinylConfigurationInputValues.numbersColorId = vinylColorSelection;
        }

        if (vinylFontSelection === "") {
          setVinylConfigurationNumbersFontIdError("is required");
          return;
        } else {
          vinylConfigurationInputValues.numbersFontId = vinylFontSelection;
        }
      }
    }

    setIsAddingCustomizationArea(true);
    resetGeneralErrors([]);
    resetInputErrors();

    const variables = {
      customizationAreaTemplateId: customizationAreaTemplateSelectedRadioValue,
      method: productCustomizationMethodComboboxSelection,
      note: customizationAreaNote,
      orderProductionJobId: orderProductionJobId,
      distressed:
        availableOptions.distressedOptionIsAvailable &&
        distressedCheckboxChecked,
      halftones:
        availableOptions.halftonesOptionIsAvailable && halftonesCheckboxChecked,
      flatten:
        availableOptions.flattenOptionIsAvailable && flattenCheckboxChecked,
      recreate:
        availableOptions.recreateOptionIsAvailable && recreateCheckboxChecked,
      sponsorback:
        availableOptions.sponsorbackOptionIsAvailable &&
        sponsorbackCheckboxChecked,
      autobase:
        availableOptions.autobaseOptionIsAvailable && autobaseCheckboxChecked,
      imageRepair:
        availableOptions.imageRepairOptionIsAvailable &&
        imageRepairCheckboxChecked,
      vinylConfiguration:
        vinylConfigurationInputValues,
      splitIntoColors:
        availableOptions.colorOptionIsAvailable &&
        splitIntoColorsCheckboxChecked,
      colorCount:
        availableOptions.colorOptionIsAvailable &&
        splitIntoColorsCheckboxChecked
          ? 0
          : parseInt(colorCountInputValue, 10),
      colors:
        availableOptions.colorOptionIsAvailable &&
        splitIntoColorsCheckboxChecked
          ? colorSelections.map(colorSelection => ({
              inkColorId: colorSelection.inkColor && colorSelection.inkColor.id,
              pantoneColorId:
                colorSelection.pantoneColor && colorSelection.pantoneColor.id
            }))
          : []
    };
    addCustomizationAreaToOrderProductionJob({
      variables
    })
      .then(
        ({
          data: {
            addCustomizationAreaToOrderProductionJob: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            resetInputErrors();
            resetGeneralErrors();
            onRequestClose();
            resetForm();
          } else {
            setInputErrors(errors);
            setInkColorSelectionInputErrors([...errors.colors]);
          }
        }
      )
      .catch(e => setGeneralErrors([e.message]))
      .finally(() => setIsAddingCustomizationArea(false));
  };

  return (
    <Dialog
      aria-describedby="Add Customization Area Modal"
      aria-labelledby="AddCustomizationAreaModal"
      open={isOpen}
      onClose={handleModalRequestClose}
      fullWidth={true}
      maxWidth="sm"
    >
      <DialogTitle>Add Customization Area</DialogTitle>
      {!data ? (
        <PendingStatusView status="Waiting" />
      ) : loading ? (
        <PendingStatusView status="Loading" />
      ) : error ? (
        <div>
          <Typography variant="body2">{error.message}</Typography>
        </div>
      ) : (
        <DialogContent>
          {generalErrors && generalErrors.length > 0 ? (
            <Typography color="error">{generalErrors.join(", ")}</Typography>
          ) : isAddingCustomizationArea ? (
            <PendingStatusView status="Loading" />
          ) : (
            <React.Fragment>
              <div className={classes.printAreaMethodAttributesContainer}>
                <div className={classes.areaContainer}>
                  <Typography variant="subtitle2">Print Area</Typography>
                  <CustomizationAreaRadioList
                    iconMethod={getIconForCustomizationAreaTemplate}
                    options={
                      data.orderProductionJob
                        .availableCustomizationAreaTemplates &&
                      data.orderProductionJob.availableCustomizationAreaTemplates.map(
                        ({ id, description }) => ({
                          id,
                          label: description,
                          icon: description
                        })
                      )
                    }
                    onSelect={handleCustomizationAreaRadioListSelect}
                    selectionId={customizationAreaTemplateSelectedRadioValue}
                  />
                </div>
                <div className={classes.methodContainer}>
                  <Typography variant="subtitle2">Print Method</Typography>
                  <CustomizationAreaRadioList
                    iconMethod={getIconForProductionJobMethod}
                    options={
                      availableCustomizationAreaMethods &&
                      availableCustomizationAreaMethods.map(
                        ({ id, label }) => ({
                          id,
                          label,
                          icon: id
                        })
                      )
                    }
                    onSelect={handleProductCustomizationMethodComboboxSelect}
                    selectionId={productCustomizationMethodComboboxSelection}
                  />
                </div>
                <div className={classes.attributesContainer}>
                  <Typography variant="subtitle2">Attributes</Typography>
                  <FormControl>
                    {availableOptions.distressedOptionIsAvailable && (
                      <FormControlLabel
                        error={distressed.join(", ")}
                        control={
                          <Checkbox
                            checked={distressedCheckboxChecked}
                            onChange={() =>
                              handleDistressedCheckboxChange(
                                !distressedCheckboxChecked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Distressed"
                      />
                    )}
                    {availableOptions.halftonesOptionIsAvailable && (
                      <FormControlLabel
                        error={halftones.join(", ")}
                        control={
                          <Checkbox
                            checked={halftonesCheckboxChecked}
                            onChange={() =>
                              handleHalftonesCheckboxChange(
                                !halftonesCheckboxChecked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Halftones"
                      />
                    )}
                    {availableOptions.flattenOptionIsAvailable && (
                      <FormControlLabel
                        error={flatten.join(", ")}
                        control={
                          <Checkbox
                            checked={flattenCheckboxChecked}
                            onChange={() =>
                              handleFlattenCheckboxChange(
                                !flattenCheckboxChecked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Flatten"
                      />
                    )}
                    {availableOptions.recreateOptionIsAvailable && (
                      <FormControlLabel
                        error={recreate.join(", ")}
                        control={
                          <Checkbox
                            checked={recreateCheckboxChecked}
                            onChange={() =>
                              handleRecreateCheckboxChange(
                                !recreateCheckboxChecked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Recreate"
                      />
                    )}
                    {availableOptions.sponsorbackOptionIsAvailable && (
                      <FormControlLabel
                        error={sponsorback.join(", ")}
                        control={
                          <Checkbox
                            checked={sponsorbackCheckboxChecked}
                            onChange={() =>
                              handleSponsorbackCheckboxChange(
                                !sponsorbackCheckboxChecked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Sponsorback"
                      />
                    )}
                    {availableOptions.autobaseOptionIsAvailable && (
                      <FormControlLabel
                        error={autobase.join(", ")}
                        control={
                          <Checkbox
                            checked={autobaseCheckboxChecked}
                            onChange={() =>
                              handleAutobaseCheckboxChange(
                                !autobaseCheckboxChecked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Autobase"
                      />
                    )}
                    {availableOptions.imageRepairOptionIsAvailable && (
                      <FormControlLabel
                        error={imageRepair.join(", ")}
                        control={
                          <Checkbox
                            checked={imageRepairCheckboxChecked}
                            onChange={() =>
                              handleImageRepairCheckboxChange(
                                !imageRepairCheckboxChecked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="ImageRepair"
                      />
                    )}
                  </FormControl>
                </div>
              </div>
              {availableOptions.isCutVinyl ? (
                <React.Fragment>
                  <div>
                    <Typography variant="subtitle2">
                      Vinyl Configuration
                    </Typography>
                    <div>
                      <FormControlLabel
                        error={names.join(", ")}
                        control={
                          <Checkbox
                            checked={vinylNamesCheckboxChecked}
                            onChange={() =>
                              handleVinylNamesCheckboxChange(
                                !vinylNamesCheckboxChecked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Names"
                      />
                      <FormControlLabel
                        error={numbers.join(", ")}
                        control={
                          <Checkbox
                            checked={vinylNumbersCheckboxChecked}
                            onChange={() =>
                              handleVinylNumbersCheckboxChange(
                                !vinylNumbersCheckboxChecked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Numbers"
                      />
                      {(vinylNamesCheckboxChecked ||
                        vinylNumbersCheckboxChecked) && (
                        <React.Fragment>
                          <VinylCombobox
                            label="Vinyl Size"
                            options={vinylSizeOptions.map(option => ({
                              color: null,
                              id: option.id,
                              label: option.label,
                              value: option.value
                            }))}
                            errorText={size}
                            onSelect={handleVinylSizeComboboxSelect}
                          />
                          <VinylCombobox
                            label="Vinyl Color"
                            options={
                              data &&
                              data.vinylColors.map(vinylColor => ({
                                color: vinylColor.inkColor.color,
                                id: vinylColor.id,
                                label: vinylColor.inkColor.name,
                                value: vinylColor.id
                              }))
                            }
                            errorText={[...namesColorId, ...numbersColorId]}
                            onSelect={handleVinylColorSelectorSelect}
                          />
                          <VinylCombobox
                            label="Vinyl Font"
                            options={
                              data &&
                              data.vinylFonts.map(vinylFont => ({
                                color: null,
                                id: vinylFont.id,
                                label: vinylFont.alias,
                                value: vinylFont.id
                              }))
                            }
                            errorText={[...namesFontId, ...numbersFontId]}
                            onSelect={handleVinylFontSelectorSelect}
                          />
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              ) : availableOptions.colorOptionIsAvailable ? (
                <React.Fragment>
                  <div className={classes.inkColorContainer}>
                    <Typography variant="subtitle2">Colors</Typography>
                    <FormControlLabel
                      className={classes.splitIntoColorsCheckbox}
                      error={splitIntoColors.join(", ")}
                      control={
                        <Checkbox
                          checked={splitIntoColorsCheckboxChecked}
                          onChange={() =>
                            handleSplitIntoColorsCheckboxChange(
                              !splitIntoColorsCheckboxChecked
                            )
                          }
                          color="primary"
                        />
                      }
                      label="Split into colors"
                    />
                  </div>
                  {splitIntoColorsCheckboxChecked ? (
                    <React.Fragment>
                      {colorSelections.length > 0 && (
                        <ul className={classes.list}>
                          {colorSelections.map(
                            ({ inkColor, pantoneColor }, index) => (
                              <li className={classes.listItem} key={index}>
                                <InkColorDetails
                                  inkColor={
                                    inkColor
                                      ? filter(
                                          inkColorDetailsFragments.inkColor,
                                          inkColor
                                        )
                                      : null
                                  }
                                  pantoneColor={
                                    pantoneColor
                                      ? filter(
                                          inkColorDetailsFragments.pantoneColor,
                                          pantoneColor
                                        )
                                      : null
                                  }
                                />
                              </li>
                            )
                          )}
                        </ul>
                      )}
                      <Button
                        className={classes.addColorsButton}
                        color="secondary"
                        variant="contained"
                        onClick={handleAddColorsButtonClick}
                      >
                        {colorSelections.length > 0
                          ? "Update Colors"
                          : "Add Colors"}
                      </Button>
                      <InkColorModal
                        isOpen={isAddingColors}
                        inputErrors={inkColorSelectionInputErrors}
                        onRequestClose={handleAddColorsModalRequestClose}
                        onColorsAdded={handleAddColorsModalColorsAdded}
                        title="Add Ink Colors"
                      />
                    </React.Fragment>
                  ) : (
                    <div>
                      <TextField
                        className={classes.colorCount}
                        id="Color Count Input"
                        label="Color Count"
                        type="number"
                        onChange={handleColorCountInputChange}
                        variant="outlined"
                      />
                    </div>
                  )}
                </React.Fragment>
              ) : null}
              <FormControl fullWidth={true}>
                <TextField
                  helperText={note}
                  fullWidth={true}
                  label="Customization Area Notes"
                  onChange={handleCustomizationAreaNoteChange}
                  multiline={true}
                  variant="outlined"
                  value={customizationAreaNote}
                />
              </FormControl>
            </React.Fragment>
          )}
          <DialogActions>
            <Button
              color="secondary"
              disabled={isAddingCustomizationArea}
              onClick={handleCancelButtonClick}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isAddingCustomizationArea}
              onClick={handleFormSubmit}
            >
              Save
            </Button>
          </DialogActions>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default AddCustomizationAreaModal;
